import { getExtension } from '@medplum/core';
import type { ServiceRequest } from '@medplum/fhirtypes';
import { DATA_FROM_HEALTHIE_EXTENSION } from '@bariendo/lib/extensions';
import { HEALTHIE_SYSTEM_URL, HEALTHIE_LAB_TEST_CODE_SYSTEM } from '@bariendo/lib/constants';

interface HealthieLabOrderPayload {
  lab_results?: unknown[];
}

function isHealthieLabOrderServiceRequest(sr: ServiceRequest): boolean {
  return sr.identifier?.some((id) => id.system === HEALTHIE_SYSTEM_URL) ?? false;
}

function hasHealthieLabTestCodings(sr: ServiceRequest): boolean {
  return sr.code?.coding?.some((c) => c.system === HEALTHIE_LAB_TEST_CODE_SYSTEM) ?? false;
}

function hasNonEmptyLabResultsFromHealthieExtension(sr: ServiceRequest): boolean {
  const raw = getExtension(sr, DATA_FROM_HEALTHIE_EXTENSION)?.valueString;
  if (!raw) return false;
  try {
    const data = JSON.parse(raw) as HealthieLabOrderPayload;
    return Array.isArray(data.lab_results) && data.lab_results.length > 0;
  } catch {
    return false;
  }
}

/**
 * Whether a lab-order Communication should appear on the patient timeline.
 * Non-Healthie orders are always kept. Healthie migrated stubs (generic "Lab order", empty
 * lab_results, no lab-test-codes) are dropped when we have embedded ServiceRequest data.
 */
export function shouldIncludeHealthieLabOrderInTimeline(serviceRequests: ServiceRequest[]): boolean {
  if (serviceRequests.length === 0) {
    return true;
  }

  const healthieSrs = serviceRequests.filter(isHealthieLabOrderServiceRequest);
  if (healthieSrs.length === 0) {
    return true;
  }

  return healthieSrs.some((sr) => hasHealthieLabTestCodings(sr) || hasNonEmptyLabResultsFromHealthieExtension(sr));
}
